/* ==========================================================================
   utopia-configurator.js  (Oct 2026)
   Turns the configurator into a single page of collapsible rows, MAINGEAR
   style, with a live "Your build" summary in the sidebar.

   Loaded by templates whose configurator mount has the ucfg class: either
   #configure (desktop landing pages, Sonox, older laptop pages, standalone
   configurator pages) or an element marked data-ucfg-mount (the Mech 16
   configurator shell). Starts when the configurator markup is present:
   at once if it is rendered inline, on rn-config-loader.js's rn:config-ready
   event, or (for a data-ucfg-mount) as soon as any loader drops it in.

   WHAT IT CHANGES
   - Each .component collapses to a row: icon (or the chosen product's
     thumbnail), category name, current pick. One row open at a time.
   - Steps 2 and 3 are shown as two more groups on the same page, under
     headings; the step tabs are hidden by CSS.
   - The trust strip section is moved below the last group.
   - A two-option view switch under the sidebar heading, [ PC ] and
     [ SPECIFICATION ], swaps the PC image for the full component list in
     the same space, so the sidebar never changes height. PC is always the
     default view.
   - The image area is sized from the screen height so the whole sidebar
     fits the viewport and stays pinned with nothing scrolling away.
   - The sidebar's Back button (which nothing in the theme ever handled)
     returns to the landing page at the spot the visitor left it.
   - The "Configure & Buy" heading becomes "Build your {name}", using the
     system name from the configurator's own sidebar (Helix, Sonox G4, ...).
   - A button in the sidebar header collapses the panel to the essentials
     (name, price, delivery, Add to Basket, contact details). Remembered for
     the visit.
   - A quiet finance line under the price ("Spread the cost at checkout",
     with the providers beneath) and an info popover. Text only: payment is
     still chosen at checkout, nothing about checkout or pricing changes.

   WHAT IT DELIBERATELY DOES NOT TOUCH
   - No form inputs are added. cartBuilder() sends every checked radio and
     checkbox on the page to the basket. Rows are <button type="button">.
   - The theme's own markup and scripts are left in place. Its header <p>
     (with the .selected-* span the theme writes to) is hidden, not removed.
   - Category order is unchanged, so basket and order line order is too.
   - Price, delivery date, compatibility filtering and the basket are all
     still handled by the theme's existing code.

   WHERE THE PICKS COME FROM
   Always from the checked inputs, never from the theme's .selected-* text
   or .conf-selected classes, which can go stale (e.g. memory being swapped
   when a processor changes). sync() rebuilds every row and the summary
   after any click or change, after the processor and motherboard lists are
   reloaded, and whenever the theme hides or shows cards.

   GRACEFUL FAILURE
   All accordion styling is keyed on .ucfg-acc, which this script adds last.
   If anything here fails, the page stays as the phase 1 reskin: every
   category expanded, step tabs working.
   ========================================================================== */

(function () {
  'use strict';

  var mount = document.querySelector('.ucfg[data-ucfg-mount]') || document.getElementById('configure');
  if (!mount || !mount.classList.contains('ucfg')) return;

  var DEFAULTS = {
    components: 'Components',
    customise: 'Customise',
    accessories: 'Accessories',
    summary: 'Your build',
    none: 'None selected',
    open_label: 'Change',
    back: 'Back to {name}',
    heading: 'Build your {name}',
    view_pc: 'PC',
    view_spec: 'Specification',
    finance_show: 'true',
    finance_title: 'Spread the cost at checkout',
    finance_providers: 'Klarna \u00b7 PayPal \u00b7 Dopple',
    finance_info: 'Flexible payment options are available at checkout through Klarna, PayPal and Dopple, subject to eligibility and provider terms.'
  };

  var CAN_BE_EMPTY = ['finance_providers', 'finance_info'];

  var S = DEFAULTS;
  var rows = [];          /* { comp, button, media, pick, body, name, key } */
  var summaryList = null;
  var inner = null;       /* .sidebar__inner */
  var viewTabs = [];      /* [PC tab, Specification tab] */
  var COLLAPSE_KEY = 'ucfg-panel-collapsed';
  var collapseBtn = null;

  /* sticky-sidebar.js is started with topSpacing: 80 in floating-sidebar.liquid. */
  var STICKY_TOP = 80;
  var STICKY_BOTTOM = 16;
  var STAGE_MIN = 160;
  var STAGE_MAX = 480;
  var scheduled = false;
  var uid = 0;

  /* ---- helpers ----------------------------------------------------------- */

  function readSettings() {
    var node = document.querySelector('[data-ucfg-settings]');
    var out = {};
    var parsed = {};
    if (node) {
      try { parsed = JSON.parse(node.textContent) || {}; } catch (e) { parsed = {}; }
    }
    Object.keys(DEFAULTS).forEach(function (k) {
      out[k] = (typeof parsed[k] === 'string' && parsed[k].trim()) ? parsed[k].trim() : DEFAULTS[k];
      /* These two may be blanked in the Theme Editor to hide that part. */
      if (CAN_BE_EMPTY.indexOf(k) !== -1 && k in parsed &&
          (parsed[k] === null || (typeof parsed[k] === 'string' && !parsed[k].trim()))) {
        out[k] = '';
      }
    });
    return out;
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  /* The category name is the bare text inside the theme's header <p>, around
     the icon, the "Selected:" button and the .selected-* span. */
  function headerName(p) {
    var parts = [];
    for (var i = 0; i < p.childNodes.length; i++) {
      var n = p.childNodes[i];
      if (n.nodeType === 3) parts.push(n.textContent);
    }
    return parts.join(' ').replace(/\s+/g, ' ').trim();
  }

  /* The system's name as the theme prints it in the sidebar header
     (floating-sidebar.liquid): the base product's title, e.g. "Helix", or
     "Sonox G4" for workstations. */
  function systemName(root) {
    var node = root.querySelector('.system-name');
    return node ? node.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  function withName(template, name) {
    return template.replace('{name}', name).replace(/\s+/g, ' ').trim();
  }

  function setHeading(root) {
    var h = root.querySelector('h2.newh1');
    var name = systemName(root);
    if (!h || !name) return;   /* no name found: leave the theme's heading */
    h.textContent = withName(S.heading, name);
  }

  function cardFor(input) {
    return input.closest('.conf_grid__item') || input.closest('.grid-product__wrapper') || input.parentNode;
  }

  function titleOf(card) {
    var t = card.querySelector('[id="product_title"]');
    if (t && t.textContent.trim()) return t.textContent.trim();
    var v = card.querySelector('.conf-grid-product__title');
    return v ? v.textContent.trim() : '';
  }

  /* Liquid cards carry a ready-made 370px URL in #thumbnail-src and lazy-load
     their visible image, so a collapsed card may have no src yet. Cards built
     by the processor/motherboard scripts have a plain src. */
  function thumbOf(card) {
    var t = card.querySelector('[id="thumbnail-src"]');
    if (t && t.textContent.trim()) return t.textContent.trim();
    var img = card.querySelector('img');
    if (!img) return '';
    var src = img.getAttribute('src');
    if (src && src.indexOf('data:') !== 0) return src;
    var data = img.getAttribute('data-src');
    return data ? data.replace('{width}', '180') : '';
  }

  function picksFor(row) {
    if (!row.body) return [];
    var inputs = row.body.querySelectorAll('.sel-prod:checked');
    var out = [];
    for (var i = 0; i < inputs.length; i++) {
      var card = cardFor(inputs[i]);
      out.push({ title: titleOf(card), thumb: thumbOf(card) });
    }
    return out;
  }

  function refreshSticky() {
    /* floating-sidebar.liquid creates `var sidebar = new StickySidebar(...)`
       at global scope. Content height changes when rows open and close.
       Where the configurator's own scripts run after this one has started
       (the Mech 16 shell), the instance appears later, so the measurement
       correction is retried here until it has been applied. */
    correctSticky();
    try {
      if (window.sidebar && typeof window.sidebar.updateSticky === 'function') {
        window.sidebar.updateSticky();
      }
    } catch (e) { /* never let this break the page */ }
  }

  /* sticky-sidebar.js assumes the panel starts at the very top of its
     container (.wrapper). Ours starts lower, under the "Configure & Buy"
     heading, so near the bottom of the page it misplaced the panel by that
     distance: a jump at the hand-over and an overhang past the end of the
     configurator. This measures from where the panel actually starts. Only
     this page's instance is adjusted; the library file is untouched. */
  function correctSticky() {
    var s = window.sidebar;
    if (!s || s._ucfgCorrected || typeof s.calcDimensions !== 'function') return;
    var original = s.calcDimensions;
    s.calcDimensions = function () {
      original.apply(this, arguments);
      var d = this.dimensions;
      if (!d || !this.sidebar) return;
      var top = this.sidebar.getBoundingClientRect().top + (window.pageYOffset || document.documentElement.scrollTop || 0);
      if (!(top > d.containerTop) || top >= d.containerBottom) return;
      d.containerTop = top;
      d.containerHeight = d.containerBottom - top;
      d.maxTranslateY = d.containerHeight - d.sidebarHeight;
    };
    s._ucfgCorrected = true;
  }

  function nudgeLazyImages() {
    try {
      if (window.lazySizes && window.lazySizes.loader) window.lazySizes.loader.checkElems();
    } catch (e) { /* ignore */ }
  }

  /* ---- open / close ------------------------------------------------------ */

  function setOpen(row, open) {
    row.comp.classList.toggle('is-open', open);
    row.button.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  function openRow(row, scroll) {
    var wasOpen = row.comp.classList.contains('is-open');
    rows.forEach(function (r) { setOpen(r, false); });
    if (!wasOpen) setOpen(row, true);

    nudgeLazyImages();
    refreshSticky();

    if (scroll && !wasOpen) {
      /* After layout settles, so closing the previous row has already
         shortened the page. scroll-margin-top in the CSS clears the header. */
      window.requestAnimationFrame(function () {
        var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        row.comp.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      });
    }
  }

  /* ---- build ------------------------------------------------------------- */

  function buildRow(comp) {
    var header = comp.querySelector('.component-header');
    var p = header && header.querySelector('p');
    if (!p) return null;

    var name = headerName(p);
    var body = comp.querySelector('.config-body');
    var id = 'ucfg-panel-' + (++uid);
    if (body && !body.id) body.id = id;

    var button = el('button', 'ucfg-row');
    button.type = 'button';
    button.setAttribute('aria-expanded', 'false');
    if (body) button.setAttribute('aria-controls', body.id);

    var media = el('span', 'ucfg-row__media');
    media.setAttribute('aria-hidden', 'true');
    var icon = p.querySelector(':scope > i, :scope > svg');
    if (icon) {
      var iconWrap = el('span', 'ucfg-row__icon');
      iconWrap.appendChild(icon.cloneNode(true));
      media.appendChild(iconWrap);
    }
    var thumb = el('img', 'ucfg-row__thumb');
    thumb.alt = '';
    thumb.loading = 'lazy';
    thumb.hidden = true;
    media.appendChild(thumb);

    var text = el('span', 'ucfg-row__text');
    var hint = el('span', 'ucfg-visually-hidden', S.open_label + ' ');
    var nameEl = el('span', 'ucfg-row__name', name);
    var pick = el('span', 'ucfg-row__pick');
    text.appendChild(hint);
    text.appendChild(nameEl);
    text.appendChild(pick);

    var sign = el('span', 'ucfg-row__sign');
    sign.setAttribute('aria-hidden', 'true');

    button.appendChild(media);
    button.appendChild(text);
    button.appendChild(sign);
    header.appendChild(button);

    var row = { comp: comp, button: button, media: media, thumb: thumb, pick: pick, body: body, name: name, last: null };
    button.addEventListener('click', function () { openRow(row, true); });
    return row;
  }

  function groupHeading(label) {
    var h = el('h3', 'ucfg-group');
    h.appendChild(el('span', 'ucfg-group__label', label));
    return h;
  }

  /* ---- view switch: [ PC ] [ SPECIFICATION ] -------------------------------
     One piece of state, .is-spec on the sidebar, decides whether the stage
     (.case) shows the PC image or the component list. The two tabs only
     set it. Both views share the stage's height, so switching never moves
     the price, delivery date or Add to Basket. Nothing here touches the
     configuration itself. */

  function setSpec(open) {
    if (!inner) return;
    inner.classList.toggle('is-spec', open);
    viewTabs.forEach(function (tab, i) {
      var selected = (i === 1) === open;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.setAttribute('tabindex', selected ? '0' : '-1');
      tab.classList.toggle('is-active', selected);
    });
  }

  function buildViewSwitch() {
    var header = inner.querySelector('.system-name');
    var stage = inner.querySelector('.case');
    if (!header || !stage) return;
    if (!stage.id) stage.id = 'ucfg-stage';

    var bar = el('div', 'ucfg-view');
    bar.setAttribute('role', 'tablist');
    bar.setAttribute('aria-label', S.summary);

    [S.view_pc, S.view_spec].forEach(function (label, i) {
      var tab = el('button', 'ucfg-view__tab', label);
      tab.type = 'button';
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', stage.id);
      tab.addEventListener('click', function () { setSpec(i === 1); });
      /* Arrow keys move between the two tabs, as tabs are expected to. */
      tab.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Home' && e.key !== 'End') return;
        e.preventDefault();
        var next = (e.key === 'Home') ? 0 : (e.key === 'End') ? 1 : 1 - i;
        setSpec(next === 1);
        viewTabs[next].focus();
      });
      viewTabs.push(tab);
      bar.appendChild(tab);
    });

    header.parentNode.insertBefore(bar, header.nextSibling);
  }

  function buildSummary(root) {
    inner = root.querySelector('.sidebar__inner');
    if (!inner) return;

    /* The list shares the image's space (.case), so switching never changes
       the sidebar's height. .sys-img itself is only hidden by CSS:
       cartBuilder() still reads its src into the order. */
    var stage = inner.querySelector('.case');
    var spec = el('div', 'ucfg-spec');
    spec.id = 'ucfg-spec';
    spec.setAttribute('aria-label', S.summary);
    summaryList = el('ol', 'ucfg-summary__list');
    spec.appendChild(summaryList);
    (stage || inner).appendChild(spec);

    buildViewSwitch();
    setSpec(false);   /* PC is always the default view */
  }

  /* ---- finance message under the price -------------------------------------
     Reassurance, not a call to action: a small card icon, one line of text,
     the providers in grey, and an info button whose popover opens on hover
     (where hover exists) or on click/tap, and closes on a tap elsewhere or
     Escape. It never cancels the tap that closes it, so tapping Add to
     Basket with the popover open still adds to basket. */

  var ICON_CARD = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><rect x="2.5" y="5" width="19" height="14" rx="1.5"/><line x1="2.5" y1="9.5" x2="21.5" y2="9.5"/><line x1="6" y1="15" x2="10" y2="15"/></svg>';
  var ICON_INFO = '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9.5"/><line x1="12" y1="11" x2="12" y2="16.5"/><circle cx="12" cy="7.6" r="0.4" fill="currentColor"/></svg>';

  function buildFinance() {
    if (!inner || S.finance_show === 'false') return;
    var price = inner.querySelector('.float-price');
    var priceBlock = price && price.closest('.displayFlex');
    if (!priceBlock || priceBlock.parentNode !== inner) return;

    var box = el('div', 'ucfg-finance');
    var line = el('div', 'ucfg-finance__line');
    var icon = el('span', 'ucfg-finance__icon');
    icon.innerHTML = ICON_CARD;
    line.appendChild(icon);
    line.appendChild(el('span', 'ucfg-finance__title', S.finance_title));

    var tip = null;
    var info = null;
    if (S.finance_info) {
      info = el('button', 'ucfg-finance__info');
      info.type = 'button';
      info.setAttribute('aria-label', 'About payment options');
      info.setAttribute('aria-expanded', 'false');
      info.setAttribute('aria-controls', 'ucfg-finance-tip');
      info.innerHTML = ICON_INFO;
      line.appendChild(info);
    }
    box.appendChild(line);

    if (S.finance_providers) box.appendChild(el('p', 'ucfg-finance__providers', S.finance_providers));

    if (info) {
      tip = el('div', 'ucfg-finance__tip', S.finance_info);
      tip.id = 'ucfg-finance-tip';
      tip.setAttribute('role', 'tooltip');
      box.appendChild(tip);
      info.setAttribute('aria-describedby', tip.id);

      /* Opens downward over the delivery block when it fits; if it would
         reach Add to Basket (a narrow sidebar wraps the text onto more
         lines), it opens upward over the bottom of the image instead. */
      var place = function () {
        box.classList.remove('is-up');
        tip.style.bottom = '';
        var basket = inner.querySelector('.sidebarButtonFlex');
        if (!basket) return;
        var shown = getComputedStyle(tip).display !== 'none';
        if (!shown) { tip.style.visibility = 'hidden'; tip.style.display = 'block'; }
        var tipBottom = tip.getBoundingClientRect().bottom;
        if (!shown) { tip.style.display = ''; tip.style.visibility = ''; }
        if (tipBottom > basket.getBoundingClientRect().top - 6) {
          box.classList.add('is-up');
          /* CSS bottom is measured from the finance block's bottom edge. */
          tip.style.bottom = (box.offsetHeight + box.offsetTop - priceBlock.offsetTop + 6) + 'px';
        }
      };
      var setTip = function (open) {
        if (open) place();
        box.classList.toggle('is-open', open);
        info.setAttribute('aria-expanded', open ? 'true' : 'false');
      };
      info.addEventListener('mouseenter', place);
      info.addEventListener('click', function () { setTip(!box.classList.contains('is-open')); });
      document.addEventListener('click', function (e) {
        if (box.classList.contains('is-open') && !box.contains(e.target)) setTip(false);
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && box.classList.contains('is-open')) { setTip(false); info.focus(); }
      });
    }

    priceBlock.parentNode.insertBefore(box, priceBlock.nextSibling);
  }

  /* ---- collapse the whole panel to the essentials ---------------------------
     The button lives in the header row beside the system name. It carries
     no text of its own (only an aria-label), because systemName() reads the
     header's text to build the heading and the Back label. */

  function readCollapsed() {
    try { return window.sessionStorage.getItem(COLLAPSE_KEY) === '1'; } catch (e) { return false; }
  }

  function setCollapsed(collapsed) {
    if (!inner) return;
    inner.classList.toggle('is-collapsed', collapsed);
    if (collapseBtn) {
      collapseBtn.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
      collapseBtn.setAttribute('aria-label', collapsed ? 'Show build details' : 'Hide build details');
      collapseBtn.title = collapsed ? 'Show details' : 'Hide details';
    }
    try { window.sessionStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0'); } catch (e) { /* private mode */ }
    /* Expanding brings the stage back, so re-fit it to the screen. */
    if (!collapsed) fitStage();
    refreshSticky();
  }

  function buildCollapse() {
    if (!inner) return;
    var header = inner.querySelector('.system-name');
    if (!header) return;
    collapseBtn = el('button', 'ucfg-collapse');
    collapseBtn.type = 'button';
    collapseBtn.addEventListener('click', function () {
      setCollapsed(!inner.classList.contains('is-collapsed'));
    });
    header.appendChild(collapseBtn);
    setCollapsed(readCollapsed());
  }

  /* ---- fit the sidebar to the screen ---------------------------------------
     Everything in the sidebar except the image/list stage has a natural
     height. The stage gets whatever is left of the viewport (less the
     sticky offsets), clamped to STAGE_MIN..STAGE_MAX, so the whole panel
     fits and sticky-sidebar.js can pin it at the top. The image and the
     list share the same height, so toggling between them never jumps. */

  function fitStage() {
    if (!inner || window.innerWidth < 769 || !inner.offsetHeight) return;
    if (inner.classList.contains('is-collapsed')) return;   /* stage hidden */
    var box = inner.classList.contains('is-spec')
      ? inner.querySelector('.ucfg-spec')
      : inner.querySelector('.sys-img');
    if (!box) return;
    var others = inner.offsetHeight - box.offsetHeight;
    var room = window.innerHeight - STICKY_TOP - STICKY_BOTTOM - others;
    var px = Math.round(Math.max(STAGE_MIN, Math.min(STAGE_MAX, room)));
    if (mount.style.getPropertyValue('--uc-stage') !== px + 'px') {
      mount.style.setProperty('--uc-stage', px + 'px');
      refreshSticky();
    }
  }

  function watchFit() {
    var pending = false;
    function later() {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(function () { pending = false; fitStage(); });
    }
    window.addEventListener('resize', later);
    window.addEventListener('load', later);
    /* However the panel is revealed (a class on the mount, a hidden
       attribute on a wrapper), the sidebar changes size, so re-fit then. */
    if (window.ResizeObserver && inner) new ResizeObserver(later).observe(inner);
    /* The configurator is revealed by removing .hidden from the mount. */
    new MutationObserver(later).observe(mount, { attributes: true, attributeFilter: ['class'] });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(later);
    later();
  }

  /* ---- back to the landing page ------------------------------------------
     #backBtn in floating-sidebar.liquid has never had a handler. Here it
     shows #overview again, hides the configurator and restores the scroll
     position the visitor had when they opened it. The configurator stays
     loaded, so reopening it keeps every choice. */

  var overviewScroll = 0;

  function rememberOverviewScroll() {
    /* Capture phase on window, which fires before any document listener,
       so this always runs before rn-config-loader.js reveals the
       configurator and scrolls to the top (even when the configurator is
       already loaded and that happens almost immediately). */
    window.addEventListener('click', function () {
      var overview = document.getElementById('overview');
      if (overview && !overview.classList.contains('hidden') && mount.classList.contains('hidden')) {
        overviewScroll = window.pageYOffset || document.documentElement.scrollTop || 0;
      }
    }, true);
  }

  function wireBackButton(root) {
    var back = root.querySelector('#backBtn');
    if (!back) return;
    back.setAttribute('role', 'button');
    back.setAttribute('tabindex', '0');

    /* "Back" becomes e.g. "Back to Helix", keeping the theme's arrow icon.
       Without a system name it stays a plain "Back". */
    var sysName = systemName(root);
    var label = sysName ? withName(S.back, sysName) : 'Back';
    var holder = back.querySelector('.pcButtonText') || back;
    for (var i = holder.childNodes.length - 1; i >= 0; i--) {
      if (holder.childNodes[i].nodeType === 3) holder.removeChild(holder.childNodes[i]);
    }
    holder.appendChild(document.createTextNode(' ' + label));

    function go() {
      var overview = document.getElementById('overview');
      if (!overview) { window.history.back(); return; }
      overview.classList.remove('hidden');
      mount.classList.add('hidden');
      window.scrollTo(0, overviewScroll);
    }

    back.addEventListener('click', go);
    back.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); }
    });
  }

  /* ---- sync: everything visible is recomputed from checked inputs -------- */

  function sync() {
    scheduled = false;
    var lines = [];

    rows.forEach(function (row) {
      var picks = picksFor(row);
      var label = picks.length ? picks.map(function (x) { return x.title; }).join(', ') : S.none;
      var thumbSrc = picks.length === 1 ? picks[0].thumb : '';
      var key = label + '|' + thumbSrc;

      /* A category with no options at all (on some laptops a shared category
         has no products tagged for that model) is hidden rather than shown
         as an empty "None selected" row. Cards hidden by compatibility
         filtering still count: the category exists, it is just narrowed. */
      row.comp.classList.toggle('is-empty', !row.body || !row.body.querySelector('.sel-prod'));

      if (row.last !== key) {
        row.last = key;
        row.pick.textContent = label;
        row.comp.classList.toggle('has-pick', picks.length > 0);
        if (thumbSrc) {
          if (row.thumb.getAttribute('src') !== thumbSrc) row.thumb.setAttribute('src', thumbSrc);
          row.thumb.hidden = false;
          row.media.classList.add('has-thumb');
        } else {
          row.thumb.hidden = true;
          row.media.classList.remove('has-thumb');
        }
      }
      if (picks.length) lines.push({ row: row, label: label });
    });

    if (summaryList) {
      var key = lines.map(function (l) { return l.row.name + ':' + l.label; }).join('\n');
      if (summaryList.getAttribute('data-key') !== key) {
        summaryList.setAttribute('data-key', key);
        summaryList.textContent = '';
        lines.forEach(function (l) {
          var li = el('li');
          var b = el('button', 'ucfg-summary__item');
          b.type = 'button';
          b.appendChild(el('span', 'ucfg-summary__cat', l.row.name));
          b.appendChild(el('span', 'ucfg-summary__pick', l.label));
          b.addEventListener('click', function () {
            if (!l.row.comp.classList.contains('is-open')) openRow(l.row, true);
            else l.row.comp.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
          li.appendChild(b);
          summaryList.appendChild(li);
        });
        refreshSticky();
      }
    }
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(sync);
  }

  function watch(root) {
    /* Direct picks. */
    root.addEventListener('change', schedule, true);
    root.addEventListener('click', function () { window.setTimeout(schedule, 0); }, true);

    /* Programmatic changes. The theme's compatibility code re-checks inputs
       with .prop('checked') and always toggles .hidden / .conf-selected at
       the same time; the processor and motherboard lists are rebuilt
       wholesale. Both show up here. Our own writes are skipped. */
    var ours = '.ucfg-row, .ucfg-spec, .ucfg-view, .ucfg-group, .ucfg-finance';
    var mo = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var t = records[i].target;
        if (t.nodeType === 1 && t.closest && t.closest(ours)) continue;
        schedule();
        return;
      }
    });
    mo.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });

    /* Belt and braces for the AJAX-built lists. */
    if (window.jQuery) {
      try { window.jQuery(document).on('ajaxComplete', function () { window.setTimeout(schedule, 0); }); } catch (e) { /* ignore */ }
    }
  }

  function init() {
    if (mount.getAttribute('data-ucfg-ready')) return;
    var root = mount.querySelector('[data-rn-config-fragment]');
    if (!root) return;
    mount.setAttribute('data-ucfg-ready', '1');

    try {
      S = readSettings();

      var groups = [
        { id: 'configure-grid', label: S.components },
        { id: 'customise-grid', label: S.customise },
        { id: 'accessories-grid', label: S.accessories }
      ];

      groups.forEach(function (g) {
        var grid = document.getElementById(g.id);
        if (!grid) return;
        grid.classList.remove('hidden');
        grid.parentNode.insertBefore(groupHeading(g.label), grid);

        var comps = grid.querySelectorAll('.component');
        for (var i = 0; i < comps.length; i++) {
          var c = comps[i];
          if (c.classList.contains('hidden')) continue;   /* the base product block */
          var row = buildRow(c);
          if (row) rows.push(row);
        }
      });

      /* Trust strip: section rendered in the template, moved below the last group. */
      var trust = document.querySelector('[data-ucfg-trust]');
      var lastGrid = document.getElementById('accessories-grid') || document.getElementById('configure-grid');
      if (trust && lastGrid) {
        var wrap = trust.closest('.shopify-section') || trust;
        lastGrid.parentNode.insertBefore(wrap, lastGrid.nextSibling);
      }

      setHeading(root);
      buildSummary(root);
      wireBackButton(root);
      buildFinance();
      buildCollapse();
      watch(root);
      sync();
      correctSticky();
      watchFit();

      mount.classList.add('ucfg-acc');
      refreshSticky();
    } catch (err) {
      /* Leave the phase 1 layout in place rather than a half-built one:
         CSS hides everything this script added unless .ucfg-acc is set. */
      mount.classList.remove('ucfg-acc');
      ['customise-grid', 'accessories-grid'].forEach(function (id) {
        var g = document.getElementById(id);
        if (g) g.classList.add('hidden');
      });
      if (window.console) console.error('[ucfg]', err);
    }
  }

  rememberOverviewScroll();
  mount.addEventListener('rn:config-ready', init);
  if (mount.querySelector('[data-rn-config-fragment]')) {
    init();
  } else if (mount.hasAttribute('data-ucfg-mount')) {
    /* A loader that sends no event (the Mech 16 shell): start once its
       fragment has been dropped in, after its scripts have had their turn. */
    var arrival = new MutationObserver(function () {
      if (!mount.querySelector('[data-rn-config-fragment]')) return;
      arrival.disconnect();
      window.setTimeout(init, 0);
    });
    arrival.observe(mount, { childList: true });
  }
})();